import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { ExternalLink, MessageCircle, Phone, Plus, Trash2 } from 'lucide-react';
import { TabbedPage } from '../../features/common/TabbedPage';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSTable } from '../../components/ILMSTable';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { ILMSFormField, ILMSInput, ILMSSelect, ILMSTextarea } from '../../components/ILMSForm';
import { ILMSPagination, DEFAULT_PAGE_SIZE } from '../../components/ILMSPagination';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatDate, formatDateTime } from '../../utils/format';
import { cn } from '../../utils/cn';
import { mediaUrl } from '../../utils/site';
import { rupees } from '../../shop/shopApi';
import { PhotoPicker } from '../admin/appearance/PhotoPicker';
import { shopAdminApi, type AdminOrder, type AdminProduct, type ShopSettings } from './shopAdminApi';
import { useSearchParams } from 'react-router-dom';
import { CustomerAccountsTab, CustomerPaymentsTab } from '../procurement/CustomerBilling';
import { HubPaymentsTab } from '../dairy/HubPayments';
import { CustomersTab, DeliveryChangesTab, SubscriptionPlansTab } from './CustomerTabs';
import { AgentsTab } from './AgentsTab';
import type { Tone } from '../../utils/statusTone';

// The website's online shop, run from here (Sales permission): check each
// UPI payment against the UPI business app, then confirm, deliver, cancel;
// products and prices; the UPI ID and delivery rules.

const PAY_TONE: Record<string, Tone> = {
  'Awaiting payment': 'neutral', 'Paid - to verify': 'warning', Verified: 'success', 'Not received': 'critical', Refunded: 'info', 'Cash on delivery': 'info',
};
const STATUS_TONE: Record<string, Tone> = { Placed: 'neutral', Confirmed: 'info', 'Out for delivery': 'info', Delivered: 'success', Cancelled: 'critical' };

const VIEWS = [
  { id: 'to_verify', label: 'Check payment', key: 'to_verify' as const },
  { id: 'to_confirm', label: 'To confirm', key: 'to_confirm' as const },
  { id: 'to_deliver', label: 'To deliver' },
  { id: 'awaiting_payment', label: 'Not paid yet', key: 'awaiting_payment' as const },
  { id: 'refund_due', label: 'Refund due', key: 'refund_due' as const },
  { id: 'delivered', label: 'Delivered' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: '', label: 'All' },
];

function OrderDetail({ order, onClose }: { order: AdminOrder; onClose: () => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('Sales', 'Edit');
  const [o, setO] = useState(order);
  const [reason, setReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [note, setNote] = useState('');
  const [notePublic, setNotePublic] = useState(false);
  const run = useMutation({
    mutationFn: (fn: () => Promise<AdminOrder>) => fn(),
    onSuccess: (next) => {
      setO(next);
      setCancelling(false);
      setReason('');
      setNote('');
      qc.invalidateQueries({ queryKey: ['shop-admin'] });
    },
  });
  const act = (fn: () => Promise<AdminOrder>) => run.mutate(fn);
  const wa = `https://wa.me/91${o.mobile}?text=${encodeURIComponent(`Hello ${o.customer_name}, about your Sri KalpaGavya order ${o.ref} (${rupees(o.total)}): `)}`;
  const open = o.status !== 'Delivered' && o.status !== 'Cancelled';
  const paid = o.payment_status === 'Verified';

  return (
    <ILMSModal open onClose={onClose} title={`Order ${o.ref}`} size="lg">
      <div className="flex flex-col gap-4">
        {run.isError && <ILMSAlert tone="critical">{getApiErrorMessage(run.error)}</ILMSAlert>}
        <div className="flex flex-wrap items-center gap-2">
          <ILMSStatusBadge status={o.status} tone={STATUS_TONE[o.status]} />
          <ILMSStatusBadge status={o.payment_status} tone={PAY_TONE[o.payment_status]} />
          <span className="text-secondary text-ink-500">{o.payment_method} · placed {formatDateTime(o.created_at)}</span>
        </div>

        {o.payment_status === 'Paid - to verify' && (
          <ILMSAlert tone="warning" title={`Check ₹${Number(o.total).toFixed(2)} in your UPI app`}>
            Customer's UPI reference <b className="tracking-wider">{o.utr}</b>, sent {o.paid_claimed_at ? formatDateTime(o.paid_claimed_at) : ''}. The note on the payment should say "Order {o.ref}".
            Verify only when the money is actually in the account.
          </ILMSAlert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-body">
            <p className="font-semibold">{o.customer_name}{o.customer_id && <span className="ml-2 text-helper-text text-info-600">(existing customer)</span>}</p>
            <p className="text-secondary">{o.flat}{o.building ? `, ${o.building}` : ''}, {o.area}{o.landmark ? ` (near ${o.landmark})` : ''} — {o.pincode}</p>
            <p className="text-secondary font-medium mt-1">Deliver {formatDate(o.delivery_date)} · {o.delivery_slot}</p>
            {o.notes && <p className="text-secondary text-ink-500 mt-1">Note: {o.notes}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              <a href={`tel:+91${o.mobile}`} className="inline-flex items-center gap-1 rounded-md border border-ink-300/40 px-2.5 py-1 text-secondary"><Phone className="h-3.5 w-3.5" /> {o.mobile}</a>
              <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md border border-success-500/40 px-2.5 py-1 text-secondary text-success-700"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
            </div>
          </div>
          <div className="text-secondary">
            {o.items.map((i, n) => (
              <div key={n} className="flex justify-between py-0.5"><span>{i.name} {i.pack_label} × {i.quantity}</span><span className="tabular-nums">{rupees(i.line_total)}</span></div>
            ))}
            <div className="flex justify-between py-0.5 text-ink-500"><span>Delivery</span><span>{rupees(o.delivery_fee)}</span></div>
            <div className="flex justify-between border-t border-ink-300/30 pt-1 font-bold"><span>Total</span><span>{rupees(o.total)}</span></div>
            {o.upi_id && <p className="mt-1 text-helper-text text-ink-400">To be paid to {o.upi_id}</p>}
          </div>
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2 border-t border-ink-300/15 pt-3">
            {o.payment_method === 'UPI' && ['Paid - to verify', 'Awaiting payment', 'Not received'].includes(o.payment_status) && o.status !== 'Cancelled' && (
              <ILMSButton loading={run.isPending} onClick={() => act(() => shopAdminApi.payment(o.id, 'verify'))}>Money received — verify & confirm</ILMSButton>
            )}
            {o.payment_status === 'Paid - to verify' && (
              <ILMSButton variant="outline" onClick={() => act(() => shopAdminApi.payment(o.id, 'not_received'))}>Not received</ILMSButton>
            )}
            {o.payment_status === 'Cash on delivery' && o.status !== 'Cancelled' && (
              <ILMSButton variant="outline" onClick={() => act(() => shopAdminApi.payment(o.id, 'cash_received'))}>Cash received</ILMSButton>
            )}
            {o.status === 'Placed' && (paid || o.payment_status === 'Cash on delivery') && (
              <ILMSButton onClick={() => act(() => shopAdminApi.status(o.id, 'Confirmed'))}>Confirm order</ILMSButton>
            )}
            {o.status === 'Confirmed' && <ILMSButton onClick={() => act(() => shopAdminApi.status(o.id, 'Out for delivery'))}>Out for delivery</ILMSButton>}
            {(o.status === 'Confirmed' || o.status === 'Out for delivery') && (
              <ILMSButton variant="secondary" onClick={() => act(() => shopAdminApi.status(o.id, 'Delivered'))}>Delivered</ILMSButton>
            )}
            {o.status === 'Cancelled' && ['Verified', 'Paid - to verify'].includes(o.payment_status) && (
              <ILMSButton variant="outline" onClick={() => act(() => shopAdminApi.payment(o.id, 'refunded'))}>Refund sent</ILMSButton>
            )}
            {open && !cancelling && <ILMSButton variant="danger" onClick={() => setCancelling(true)}>Cancel order…</ILMSButton>}
          </div>
        )}
        {cancelling && (
          <div className="flex flex-col gap-2 rounded-md border border-critical-500/30 bg-critical-50 p-3">
            <ILMSFormField label="Reason (the customer sees it)" htmlFor="cancel_reason">
              <ILMSInput id="cancel_reason" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Sorry, we couldn't deliver to your area today" />
            </ILMSFormField>
            {paid && <p className="text-helper-text text-critical-700">This order was paid — after cancelling, send {rupees(o.total)} back by UPI and press "Refund sent".</p>}
            <div className="flex gap-2">
              <ILMSButton variant="danger" disabled={!reason.trim()} onClick={() => act(() => shopAdminApi.status(o.id, 'Cancelled', reason))}>Cancel order</ILMSButton>
              <ILMSButton variant="ghost" onClick={() => setCancelling(false)}>Keep it</ILMSButton>
            </div>
          </div>
        )}

        <div>
          <p className="text-label mb-1.5">Timeline</p>
          <ul className="flex flex-col gap-1.5">
            {o.events.map((e, i) => (
              <li key={i} className="text-secondary">
                <span className="font-medium">{e.label}</span>
                <span className="text-helper-text text-ink-400"> · {formatDateTime(e.created_at)}{e.by_customer ? ' · customer' : ''}{!e.public ? ' · staff only' : ''}</span>
                {e.note && <p className="text-helper-text text-ink-500">{e.note}</p>}
              </li>
            ))}
          </ul>
          {canEdit && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <ILMSInput className="flex-1 min-w-[12rem]" placeholder="Add a note…" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
              <label className="flex items-center gap-1.5 text-secondary"><input type="checkbox" checked={notePublic} onChange={(e) => setNotePublic(e.target.checked)} /> Customer can see it</label>
              <ILMSButton size="sm" variant="outline" disabled={!note.trim()} onClick={() => act(() => shopAdminApi.note(o.id, note, notePublic))}>Add</ILMSButton>
            </div>
          )}
        </div>
      </div>
    </ILMSModal>
  );
}

function OrdersTab() {
  const [view, setView] = useState('to_verify');
  const [q, setQ] = useState('');
  const [day, setDay] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [open, setOpen] = useState<AdminOrder | null>(null);
  const summary = useQuery({ queryKey: ['shop-admin', 'summary'], queryFn: shopAdminApi.summary, refetchInterval: 60_000 });
  const list = useQuery({
    queryKey: ['shop-admin', 'orders', view, q, day, page, pageSize],
    queryFn: () => shopAdminApi.orders({ view: view || undefined, q: q || undefined, delivery_date: day || undefined, page, page_size: pageSize }),
    refetchInterval: 60_000,
  });
  const counts = summary.data;

  return (
    <ILMSCard padded={false}>
      <div className="flex flex-col gap-3 border-b border-ink-300/10 p-3">
        <div className="flex flex-wrap gap-1.5">
          {VIEWS.map((v) => {
            const n = v.key && counts ? counts[v.key] : undefined;
            return (
              <button key={v.id} type="button" onClick={() => { setView(v.id); setPage(1); }}
                className={cn('inline-flex items-center gap-1.5 rounded-full border px-3 h-8 text-secondary font-medium',
                  view === v.id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/40 text-ink-600 hover:border-brand-green-400')}>
                {v.label}
                {n ? <span className={cn('rounded-full px-1.5 text-[11px] font-bold', view === v.id ? 'bg-white/25' : v.id === 'to_verify' ? 'bg-warning-500 text-white' : 'bg-ink-300/30')}>{n}</span> : null}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-2">
          <ILMSInput className="max-w-xs" placeholder="Order no., name, mobile, flat, UPI ref…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <ILMSInput type="date" className="max-w-[11rem]" value={day} onChange={(e) => { setDay(e.target.value); setPage(1); }} title="Delivery date" />
          {day && <ILMSButton variant="ghost" size="sm" onClick={() => setDay('')}>Any day</ILMSButton>}
          <a href="/shop" target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-1 text-secondary text-info-600 hover:underline">Open the shop <ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
      </div>
      <div className="px-3 pt-1 pb-3">
        <ILMSTable
          columns={[
            { key: 'ref', header: 'Order', primary: true, render: (r) => <span className="font-semibold tracking-wide">{r.ref}</span> },
            { key: 'placed', header: 'Placed', render: (r) => formatDateTime(r.created_at), sortValue: (r) => r.created_at },
            { key: 'customer', header: 'Customer', render: (r) => `${r.customer_name} · ${r.flat}${r.building ? `, ${r.building}` : ''}` },
            { key: 'delivery', header: 'Delivery', render: (r) => `${formatDate(r.delivery_date)} ${r.delivery_slot === 'Morning' ? 'AM' : 'PM'}`, sortValue: (r) => `${r.delivery_date}${r.delivery_slot}` },
            { key: 'total', header: 'Total', align: 'right', render: (r) => rupees(r.total), sortValue: (r) => Number(r.total) },
            { key: 'payment', header: 'Payment', render: (r) => <span className="inline-flex flex-col"><ILMSStatusBadge status={r.payment_status} tone={PAY_TONE[r.payment_status]} size="sm" />{r.utr && <span className="text-helper-text text-ink-400">UTR {r.utr}</span>}</span> },
            { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} tone={STATUS_TONE[r.status]} size="sm" /> },
          ]}
          data={list.data?.items ?? []}
          rowKey={(r) => r.id}
          onRowClick={setOpen}
          exportTitle="Shop Orders" exportDateOf={(r) => r.delivery_date}
          exportRows={async () => (await shopAdminApi.orders({ view: view || undefined, q: q || undefined, page: 1, page_size: 2000 })).items}
          isLoading={list.isLoading}
          error={list.isError ? getApiErrorMessage(list.error) : null}
          emptyTitle={view === 'to_verify' ? 'No payments to check.' : 'No orders here.'}
        />
        {(list.data?.total ?? 0) > 0 && (
          <ILMSPagination page={page} pageSize={pageSize} total={list.data!.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />
        )}
      </div>
      {open && <OrderDetail order={open} onClose={() => setOpen(null)} />}
    </ILMSCard>
  );
}

const EMPTY: Partial<AdminProduct> = { name: '', category: 'Milk', pack_label: '', price: '', description: '', photo: null, active: true, in_stock: true, featured: false, max_per_order: 10, sort_order: 0 };

function ProductsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('Sales', 'Edit');
  const [edit, setEdit] = useState<Partial<AdminProduct> | null>(null);
  const list = useQuery({ queryKey: ['shop-admin', 'products'], queryFn: shopAdminApi.products });
  const save = useMutation({
    mutationFn: (p: Partial<AdminProduct>) => shopAdminApi.saveProduct(p),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shop-admin', 'products'] }); setEdit(null); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => shopAdminApi.deleteProduct(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shop-admin', 'products'] }); setEdit(null); },
  });
  const quick = (p: AdminProduct, patch: Partial<AdminProduct>) => save.mutate({ ...p, ...patch });
  const set = (k: keyof AdminProduct, v: unknown) => setEdit((cur) => ({ ...cur!, [k]: v }));

  return (
    <ILMSCard padded={false}>
      <div className="flex items-center justify-between gap-2 border-b border-ink-300/10 p-3">
        <p className="text-secondary text-ink-500">What the website sells. Untick "In stock" for a day you can't supply it; untick "On the website" to hide it.</p>
        {can('Sales', 'Create') && <ILMSButton icon={<Plus />} onClick={() => setEdit({ ...EMPTY })}>Add product</ILMSButton>}
      </div>
      <div className="px-3 pt-1 pb-3">
        <ILMSTable
          columns={[
            { key: 'photo', header: '', sortable: false, render: (p) => (p.photo ? <img src={mediaUrl(p.photo)} alt="" className="h-9 w-9 rounded object-cover" /> : <span className="inline-block h-9 w-9 rounded bg-surface-muted" />), hideOnMobile: true },
            { key: 'name', header: 'Product', primary: true, render: (p) => `${p.name} · ${p.pack_label}` },
            { key: 'category', header: 'Category', render: (p) => p.category },
            { key: 'price', header: 'Price', align: 'right', render: (p) => rupees(p.price), sortValue: (p) => Number(p.price) },
            { key: 'stock', header: 'In stock', sortable: false, render: (p) => (
              <input type="checkbox" disabled={!canEdit} checked={p.in_stock} onClick={(e) => e.stopPropagation()} onChange={(e) => quick(p, { in_stock: e.target.checked })} className="h-4 w-4" />
            ) },
            { key: 'active', header: 'On the website', sortable: false, render: (p) => (
              <input type="checkbox" disabled={!canEdit} checked={p.active} onClick={(e) => e.stopPropagation()} onChange={(e) => quick(p, { active: e.target.checked })} className="h-4 w-4" />
            ) },
            { key: 'sort', header: 'Order', align: 'right', render: (p) => p.sort_order },
          ]}
          data={list.data ?? []}
          rowKey={(p) => p.id}
          onRowClick={canEdit ? (p) => setEdit({ ...p }) : undefined}
          isLoading={list.isLoading}
          error={list.isError ? getApiErrorMessage(list.error) : null}
          emptyTitle="No products yet."
          emptyDescription="Add milk, paneer, curd… with the pack size and price."
        />
      </div>

      {edit && (
        <ILMSModal open onClose={() => setEdit(null)} title={edit.id ? 'Edit product' : 'Add product'} size="lg"
          footer={
            <div className="flex w-full items-center justify-between gap-2">
              {edit.id && can('Sales', 'Delete/Archive') ? (
                <ILMSButton variant="danger" size="sm" icon={<Trash2 />} onClick={() => { if (window.confirm('Remove this product? (If it was ever ordered it is only hidden.)')) remove.mutate(edit.id!); }}>Remove</ILMSButton>
              ) : <span />}
              <div className="flex gap-2">
                <ILMSButton variant="ghost" onClick={() => setEdit(null)}>Cancel</ILMSButton>
                <ILMSButton loading={save.isPending} disabled={!edit.name || !edit.pack_label || !(Number(edit.price) > 0)} onClick={() => save.mutate(edit)}>Save</ILMSButton>
              </div>
            </div>
          }>
          <div className="flex flex-col gap-4">
            {save.isError && <ILMSAlert tone="critical">{getApiErrorMessage(save.error)}</ILMSAlert>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ILMSFormField label="Name" htmlFor="p_name" required><ILMSInput id="p_name" maxLength={150} placeholder="e.g. Cow Milk" value={edit.name ?? ''} onChange={(e) => set('name', e.target.value)} /></ILMSFormField>
              <ILMSFormField label="Pack size" htmlFor="p_pack" required helperText="e.g. 1 L, 500 ml, 250 g"><ILMSInput id="p_pack" maxLength={40} value={edit.pack_label ?? ''} onChange={(e) => set('pack_label', e.target.value)} /></ILMSFormField>
              <ILMSFormField label="Price (₹)" htmlFor="p_price" required><ILMSInput id="p_price" type="number" min={1} step="0.5" value={edit.price ?? ''} onChange={(e) => set('price', e.target.value)} /></ILMSFormField>
              <ILMSFormField label="Category" htmlFor="p_cat" helperText="Products are grouped by this on the website."><ILMSInput id="p_cat" maxLength={60} value={edit.category ?? ''} onChange={(e) => set('category', e.target.value)} /></ILMSFormField>
              <ILMSFormField label="Most per order" htmlFor="p_max"><ILMSInput id="p_max" type="number" min={1} max={100} value={edit.max_per_order ?? 10} onChange={(e) => set('max_per_order', Number(e.target.value))} /></ILMSFormField>
              <ILMSFormField label="Position (lower shows first)" htmlFor="p_sort"><ILMSInput id="p_sort" type="number" min={0} value={edit.sort_order ?? 0} onChange={(e) => set('sort_order', Number(e.target.value))} /></ILMSFormField>
            </div>
            <ILMSFormField label="Short description" htmlFor="p_desc"><ILMSTextarea id="p_desc" rows={2} maxLength={2000} value={edit.description ?? ''} onChange={(e) => set('description', e.target.value)} /></ILMSFormField>
            <PhotoPicker label="Product photo" value={edit.photo} fallback={null} maxSide={1000} uploader={shopAdminApi.uploadPhoto} help="Square photos look best." onChange={(n) => set('photo', n)} />
            <div className="flex flex-wrap gap-4 text-secondary">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.active} onChange={(e) => set('active', e.target.checked)} /> On the website</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.in_stock} onChange={(e) => set('in_stock', e.target.checked)} /> In stock</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!edit.featured} onChange={(e) => set('featured', e.target.checked)} /> Featured</label>
            </div>
          </div>
        </ILMSModal>
      )}
    </ILMSCard>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canEdit = can('Sales', 'Edit');
  const q = useQuery({ queryKey: ['shop-admin', 'settings'], queryFn: shopAdminApi.settings });
  const [s, setS] = useState<ShopSettings | null>(null);
  const [pins, setPins] = useState('');
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (q.data) { setS(q.data); setPins(q.data.pincodes.join(', ')); } }, [q.data]);
  const save = useMutation({
    mutationFn: () => shopAdminApi.saveSettings({
      ...s!,
      // An emptied money box = ₹0.
      delivery_fee: Number(s!.delivery_fee) || 0,
      free_delivery_above: Number(s!.free_delivery_above) || 0,
      min_order: Number(s!.min_order) || 0,
      pincodes: pins.split(/[\s,]+/).filter(Boolean),
    }),
    onSuccess: (d) => { qc.setQueryData(['shop-admin', 'settings'], d); qc.invalidateQueries({ queryKey: ['shop-front'] }); setSaved(true); },
  });
  if (!s) return <ILMSCard><p className="text-secondary text-ink-500">Loading…</p></ILMSCard>;
  const set = <K extends keyof ShopSettings>(k: K, v: ShopSettings[K]) => { setSaved(false); setS({ ...s, [k]: v }); };
  // The test uses the SAVED UPI ID - the one orders are paid to.
  const live = q.data;
  const testLink = live?.upi_id ? `upi://pay?pa=${encodeURIComponent(live.upi_id)}&pn=${encodeURIComponent(live.payee_name)}&am=1.00&cu=INR&tn=${encodeURIComponent('Test payment')}` : '';
  const unsavedUpi = !!live && (s.upi_id !== live.upi_id || s.payee_name !== live.payee_name);

  return (
    <ILMSCard>
      <div className="flex flex-col gap-5 max-w-4xl">
        {save.isError && <ILMSAlert tone="critical">{getApiErrorMessage(save.error)}</ILMSAlert>}
        <div className={cn('flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3', s.accepting_orders ? 'border-success-500/40 bg-success-50' : 'border-warning-500/40 bg-warning-50')}>
          <div>
            <p className="text-card-title">{s.accepting_orders ? 'The shop is open' : 'The shop is closed'}</p>
            <p className="text-secondary text-ink-600">{s.accepting_orders ? 'Customers can order on the website.' : 'Customers can see products but cannot order.'}</p>
          </div>
          <label className="flex items-center gap-2 text-label">
            <input type="checkbox" className="h-5 w-5" disabled={!canEdit} checked={s.accepting_orders} onChange={(e) => set('accepting_orders', e.target.checked)} /> Accept orders
          </label>
        </div>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <h3 className="md:col-span-2 text-card-title">UPI payment (your current account)</h3>
          <ILMSFormField label="UPI ID of the current account" htmlFor="upi" helperText="Shown in your bank / UPI business app, e.g. srikalpagavya@okaxis">
            <ILMSInput id="upi" disabled={!canEdit} value={s.upi_id} onChange={(e) => set('upi_id', e.target.value.trim())} />
          </ILMSFormField>
          <ILMSFormField label="Name customers see when paying" htmlFor="payee">
            <ILMSInput id="payee" disabled={!canEdit} maxLength={60} value={s.payee_name} onChange={(e) => set('payee_name', e.target.value)} />
          </ILMSFormField>
          {unsavedUpi ? (
            <p className="md:col-span-2 rounded-md bg-warning-50 p-3 text-secondary text-warning-700">
              You changed the UPI ID — press <b>Save shop settings</b> first. Orders are paid to the saved UPI ID{live?.upi_id ? ` (now ${live.upi_id})` : ''}, and the test below uses that one.
            </p>
          ) : testLink && (
            <div className="md:col-span-2 flex items-center gap-4 rounded-md border border-ink-300/20 bg-surface-subtle p-3">
              <QRCodeSVG value={testLink} size={96} />
              <p className="text-secondary text-ink-600">Test the saved UPI ID <b>{live!.upi_id}</b>: scan with your own phone and pay <b>₹1</b>. If your UPI app shows <b>{live!.payee_name}</b> and the money arrives in the current account, orders will work too.</p>
            </div>
          )}
          <label className="md:col-span-2 flex items-center gap-2 text-secondary">
            <input type="checkbox" disabled={!canEdit} checked={s.cash_on_delivery} onChange={(e) => set('cash_on_delivery', e.target.checked)} /> Also allow cash on delivery
          </label>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <h3 className="md:col-span-3 text-card-title">Delivery and charges</h3>
          <ILMSFormField label="Delivery charge (₹)" htmlFor="fee"><ILMSInput id="fee" type="number" min={0} disabled={!canEdit} value={s.delivery_fee} onChange={(e) => set('delivery_fee', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Free delivery above (₹)" htmlFor="free" helperText="0 = always charge"><ILMSInput id="free" type="number" min={0} disabled={!canEdit} value={s.free_delivery_above} onChange={(e) => set('free_delivery_above', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Smallest order (₹)" htmlFor="min" helperText="0 = no minimum"><ILMSInput id="min" type="number" min={0} disabled={!canEdit} value={s.min_order} onChange={(e) => set('min_order', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Deliver to these PIN codes" htmlFor="pins" helperText="Separated by commas. Blank = anywhere." className="md:col-span-2">
            <ILMSInput id="pins" disabled={!canEdit} placeholder="560087, 562125" value={pins} onChange={(e) => { setSaved(false); setPins(e.target.value); }} />
          </ILMSFormField>
          <ILMSFormField label="Days customers can choose" htmlFor="days"><ILMSInput id="days" type="number" min={1} max={30} disabled={!canEdit} value={s.days_ahead} onChange={(e) => set('days_ahead', Number(e.target.value))} /></ILMSFormField>
          <ILMSFormField label="Delivery area note" htmlFor="areanote" className="md:col-span-2"><ILMSInput id="areanote" disabled={!canEdit} maxLength={300} placeholder="e.g. We deliver to apartments in Varthur and Gunjur." value={s.delivery_area_note} onChange={(e) => set('delivery_area_note', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Same-day orders" htmlFor="sameday">
            <ILMSSelect id="sameday" disabled={!canEdit} value={s.same_day_until ?? ''} onChange={(e) => set('same_day_until', e.target.value ? Number(e.target.value) : null)}>
              <option value="">No — from tomorrow</option>
              {[8, 10, 12, 14, 16].map((h) => <option key={h} value={h}>Yes, if ordered before {h > 12 ? `${h - 12} PM` : h === 12 ? '12 noon' : `${h} AM`}</option>)}
            </ILMSSelect>
          </ILMSFormField>
          <div className="md:col-span-3 flex flex-wrap gap-4 text-secondary">
            <span className="text-label">Delivery times:</span>
            {(['Morning', 'Evening'] as const).map((sl) => (
              <label key={sl} className="flex items-center gap-2">
                <input type="checkbox" disabled={!canEdit} checked={s.slots.includes(sl)}
                  onChange={(e) => set('slots', e.target.checked ? [...s.slots, sl].sort() as ShopSettings['slots'] : s.slots.filter((x) => x !== sl))} /> {sl}
              </label>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <h3 className="md:col-span-2 text-card-title">New customers</h3>
          <label className="md:col-span-2 flex items-center gap-2 text-secondary">
            <input type="checkbox" disabled={!canEdit} checked={s.signups_open} onChange={(e) => set('signups_open', e.target.checked)} />
            Let new customers sign up on the website (their details, address, daily milk plan and their own PIN — no office step)
          </label>
          <ILMSFormField label="How new sign-ups pay for daily milk" htmlFor="sbill" helperText="Rate always comes from the plan they pick (Subscription Plans). Delivery uses the PIN codes above.">
            <ILMSSelect id="sbill" disabled={!canEdit || !s.signups_open} value={s.signup_billing} onChange={(e) => set('signup_billing', e.target.value as ShopSettings['signup_billing'])}>
              <option value="choose">Customer chooses Prepaid or Pay later</option>
              <option value="Prepaid">Prepaid only (wallet, recharge first)</option>
              <option value="Postpaid">Pay later only (monthly bill, due in 7 days)</option>
            </ILMSSelect>
          </ILMSFormField>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <h3 className="md:col-span-2 text-card-title">Messages on the shop</h3>
          <ILMSFormField label="Notice at the top of the shop" htmlFor="notice" helperText="e.g. Paneer back on Friday. Blank = none."><ILMSInput id="notice" disabled={!canEdit} maxLength={300} value={s.notice} onChange={(e) => set('notice', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="When the shop is closed" htmlFor="paused"><ILMSInput id="paused" disabled={!canEdit} maxLength={300} value={s.paused_message} onChange={(e) => set('paused_message', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Note at checkout" htmlFor="ordernote"><ILMSTextarea id="ordernote" rows={2} disabled={!canEdit} maxLength={400} value={s.order_note} onChange={(e) => set('order_note', e.target.value)} /></ILMSFormField>
          <ILMSFormField label="Phone for the shop's Call button" htmlFor="sphone" helperText="Blank = the website's phone number."><ILMSInput id="sphone" disabled={!canEdit} maxLength={30} value={s.support_phone} onChange={(e) => set('support_phone', e.target.value)} /></ILMSFormField>
        </section>

        {canEdit && (
          <div className="flex items-center gap-3">
            <ILMSButton loading={save.isPending} onClick={() => save.mutate()}>Save shop settings</ILMSButton>
            {saved && <span className="text-helper-text text-success-700">Saved — the website shows it now.</span>}
          </div>
        )}
      </div>
    </ILMSCard>
  );
}

// Subscription customers paying their bill / recharging on the website.
function AccountPaymentsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [view, setView] = useState('to_verify');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const list = useQuery({ queryKey: ['shop-admin', 'acct', view, page, pageSize], queryFn: () => shopAdminApi.accountPayments(view, page, pageSize), refetchInterval: 60_000 });
  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'verify' | 'not_received' }) => shopAdminApi.accountPaymentAction(id, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shop-admin', 'acct'] }),
  });
  return (
    <ILMSCard padded={false}>
      <div className="flex flex-wrap items-center gap-2 border-b border-ink-300/10 p-3">
        {[{ id: 'to_verify', label: 'Check payment' }, { id: '', label: 'All' }].map((v) => (
          <button key={v.id} type="button" onClick={() => { setView(v.id); setPage(1); }}
            className={cn('rounded-full border px-3 h-8 text-secondary font-medium', view === v.id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/40 text-ink-600')}>{v.label}</button>
        ))}
        <p className="ml-auto text-helper-text text-ink-500">Verified payments go into the customer's account (Customer Payments, method "Website (UPI)").</p>
      </div>
      {act.isError && <div className="p-3"><ILMSAlert tone="critical">{getApiErrorMessage(act.error)}</ILMSAlert></div>}
      <div className="px-3 pt-1 pb-3">
        <ILMSTable
          columns={[
            { key: 'ref', header: 'Ref', primary: true, render: (r) => <span className="font-semibold tracking-wide">{r.ref}</span> },
            { key: 'when', header: 'Started', render: (r) => formatDateTime(r.created_at), sortValue: (r) => r.created_at },
            { key: 'customer', header: 'Customer', render: (r) => `${r.customer_name}${r.customer_code ? ` (${r.customer_code})` : ''} · ${r.mobile ?? ''}` },
            { key: 'amount', header: 'Amount', align: 'right', render: (r) => rupees(r.amount), sortValue: (r) => Number(r.amount) },
            { key: 'status', header: 'Status', render: (r) => <span className="inline-flex flex-col"><ILMSStatusBadge status={r.status} tone={PAY_TONE[r.status]} size="sm" />{r.utr && <span className="text-helper-text text-ink-400">UTR {r.utr}</span>}</span> },
            { key: 'act', header: '', sortable: false, render: (r) => can('Sales', 'Edit') && ['Paid - to verify', 'Not received'].includes(r.status) ? (
              <span className="flex gap-1.5">
                <ILMSButton size="sm" loading={act.isPending && act.variables?.id === r.id} onClick={() => act.mutate({ id: r.id, action: 'verify' })}>Money received</ILMSButton>
                {r.status === 'Paid - to verify' && <ILMSButton size="sm" variant="outline" onClick={() => act.mutate({ id: r.id, action: 'not_received' })}>Not received</ILMSButton>}
              </span>
            ) : null },
          ]}
          data={list.data?.items ?? []}
          rowKey={(r) => r.id}
          exportTitle="Account Payments" exportDateOf={(r) => r.created_at}
          exportRows={async () => (await shopAdminApi.accountPayments(view, 1, 2000)).items}
          isLoading={list.isLoading}
          error={list.isError ? getApiErrorMessage(list.error) : null}
          emptyTitle={view === 'to_verify' ? 'No subscription payments to check.' : 'No website payments yet.'}
        />
        {(list.data?.total ?? 0) > 0 && <ILMSPagination page={page} pageSize={pageSize} total={list.data!.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />}
      </div>
    </ILMSCard>
  );
}

// Moving customers from the app to the website: who can sign in, a new PIN,
// and a ready WhatsApp message with the link, their mobile and the PIN.
function CustomerLoginsTab() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [q, setQ] = useState('');
  const [only, setOnly] = useState<'subscribers' | 'online' | 'no-pin' | 'all'>('subscribers');
  const [shown, setShown] = useState<{ id: string; pin: string; mobile: string; name: string } | null>(null);
  const list = useQuery({ queryKey: ['shop-admin', 'logins', q], queryFn: () => shopAdminApi.customerLogins(q) });
  const make = useMutation({
    mutationFn: (c: { id: string; name: string }) => shopAdminApi.newPin(c.id).then((r) => ({ ...r, id: c.id, name: c.name })),
    onSuccess: (r) => { setShown(r); qc.invalidateQueries({ queryKey: ['shop-admin', 'logins'] }); },
  });
  const rows = (list.data ?? [])
    .filter((c) => only === 'all' || (only === 'subscribers' ? c.subscription_active : only === 'online' ? !!c.signed_up_online_at : !c.has_pin))
    .sort((a, b) => (only === 'online' ? (b.signed_up_online_at ?? '').localeCompare(a.signed_up_online_at ?? '') : 0));
  const site = `${window.location.origin}/shop/account`;
  const message = (s: NonNullable<typeof shown>) =>
    `Namaste ${s.name}, your Sri KalpaGavya milk account is now on our website: ${site}\nMobile: ${s.mobile}\nPIN: ${s.pin}\nThere you can see today's delivery code, change or pause delivery, see your milk and payments, and pay by UPI.`;
  const total = list.data?.filter((c) => c.subscription_active).length ?? 0;
  const ready = list.data?.filter((c) => c.subscription_active && c.has_pin).length ?? 0;

  return (
    <ILMSCard padded={false}>
      <div className="flex flex-col gap-2 border-b border-ink-300/10 p-3">
        <p className="text-secondary text-ink-600">
          Subscribers sign in on the website with their <b>mobile number + PIN</b> — the same PIN as the app, so anyone who already has one needs nothing new.
          <b> {ready} of {total}</b> subscribers can sign in now.
        </p>
        <div className="flex flex-wrap gap-2">
          <ILMSInput className="max-w-xs" placeholder="Name, mobile or code…" value={q} onChange={(e) => setQ(e.target.value)} />
          {([['subscribers', 'Subscribers'], ['online', 'Signed up online'], ['no-pin', 'No PIN yet'], ['all', 'All customers']] as const).map(([id, label]) => (
            <button key={id} type="button" onClick={() => setOnly(id)} className={cn('rounded-full border px-3 h-9 text-secondary font-medium', only === id ? 'border-brand-green-700 bg-brand-green-700 text-white' : 'border-ink-300/40 text-ink-600')}>{label}</button>
          ))}
        </div>
      </div>
      {make.isError && <div className="p-3"><ILMSAlert tone="critical">{getApiErrorMessage(make.error)}</ILMSAlert></div>}
      <div className="px-3 pt-1 pb-3">
        <ILMSTable
          columns={[
            { key: 'name', header: 'Customer', primary: true, render: (c) => `${c.name}${c.customer_code ? ` (${c.customer_code})` : ''}` },
            { key: 'mobile', header: 'Mobile (login)', render: (c) => c.mobile || <span className="text-critical-600">no mobile</span> },
            { key: 'type', header: 'Type', render: (c) => (
              <span className="inline-flex flex-col">
                {`${c.party_type}${c.subscription_active ? ' · subscription' : ''}`}
                {c.signed_up_online_at && <span className="text-helper-text text-brand-green-700">Signed up online {formatDateTime(c.signed_up_online_at)}</span>}
              </span>
            ) },
            { key: 'pin', header: 'Can sign in', render: (c) => <ILMSStatusBadge status={c.has_pin ? 'PIN set' : 'No PIN'} tone={c.has_pin ? 'success' : 'neutral'} size="sm" />, sortValue: (c) => (c.has_pin ? 1 : 0) },
            { key: 'act', header: '', sortable: false, render: (c) => can('Sales', 'Edit') ? (
              <ILMSButton size="sm" variant={c.has_pin ? 'ghost' : 'primary'} loading={make.isPending && make.variables?.id === c.id}
                onClick={() => { if (!c.has_pin || window.confirm(`Make a new PIN for ${c.name}? Their old PIN (app and website) stops working.`)) make.mutate(c); }}>
                {c.has_pin ? 'New PIN' : 'Make PIN'}
              </ILMSButton>
            ) : null },
          ]}
          data={rows}
          rowKey={(c) => c.id}
          exportTitle="Customer Logins"
          isLoading={list.isLoading}
          error={list.isError ? getApiErrorMessage(list.error) : null}
          emptyTitle="No customers here."
        />
      </div>
      {shown && (
        <ILMSModal open onClose={() => setShown(null)} title={`PIN for ${shown.name}`} size="sm"
          footer={<div className="flex w-full justify-end gap-2"><ILMSButton variant="ghost" onClick={() => setShown(null)}>Done</ILMSButton></div>}>
          <div className="flex flex-col gap-3">
            <p className="text-secondary">Mobile <b>{shown.mobile}</b> · PIN <b className="text-section-title tracking-[0.3em]">{shown.pin}</b></p>
            <p className="text-helper-text text-ink-500">Shown only now. Send it to the customer:</p>
            <a href={`https://wa.me/91${shown.mobile}?text=${encodeURIComponent(message(shown))}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-md bg-success-600 px-4 py-2.5 font-semibold text-white"><MessageCircle className="h-4 w-4" /> Send on WhatsApp</a>
            <ILMSButton variant="outline" onClick={() => navigator.clipboard?.writeText(message(shown))}>Copy message</ILMSButton>
          </div>
        </ILMSModal>
      )}
    </ILMSCard>
  );
}

// Tabs in the order the data flows (moved here from Procurement & Finance on
// request): what is sold (products, plans) -> who buys (customers, their
// website login) -> orders and daily delivery (changes, agents) -> money
// (website UPI payments to check -> all payments -> each customer's
// balance) -> the shop's settings. Opens on Orders, the daily work.
export default function OnlineShopPage() {
  const [searchParams] = useSearchParams();
  return (
    <TabbedPage
      title="Online Shop & Customers"
      subtitle="Products, milk customers and their plans, website orders, deliveries and agents, payments and balances — paid by UPI to the current account."
      defaultTab={searchParams.get('tab') ?? 'shop-orders'}
      tabs={[
        { id: 'shop-products', label: 'Products & Prices', render: () => <ProductsTab /> },
        { id: 'subscription-plans', label: 'Subscription Plans', render: () => <SubscriptionPlansTab /> },
        { id: 'customers', label: 'Customers', render: () => <CustomersTab /> },
        { id: 'shop-customer-logins', label: 'Customer Logins', render: () => <CustomerLoginsTab /> },
        { id: 'shop-orders', label: 'Orders', render: () => <OrdersTab /> },
        { id: 'delivery-changes', label: 'Delivery Changes', render: () => <DeliveryChangesTab /> },
        { id: 'agents', label: 'Agents (live)', render: () => <AgentsTab /> },
        { id: 'shop-account-payments', label: 'Website Payments', render: () => <AccountPaymentsTab /> },
        { id: 'customer-payments', label: 'Customer Payments', render: () => <CustomerPaymentsTab /> },
        { id: 'hub-payments', label: 'Hub Payments', render: () => <HubPaymentsTab /> },
        { id: 'customer-accounts', label: 'Customer Accounts', render: () => <CustomerAccountsTab /> },
        { id: 'shop-settings', label: 'Settings', render: () => <SettingsTab /> },
      ]}
    />
  );
}
